// src/api/axios.js
import axios from "axios";
import { setTZ, attachDateKeys } from "../utils/datesBR";

/* ===== Token getter (injeção pelo app) ===== */
let tokenGetter = null;
export function setAuthTokenGetter(fn) {
  tokenGetter = typeof fn === "function" ? fn : null;
}

// ✅ NOVO: exporta token pro realtime (não quebra nada)
export async function getAuthToken() {
  try {
    return tokenGetter ? await tokenGetter() : null;
  } catch {
    return null;
  }
}

/* ===== Actor getter ===== */
let actorGetter = null;
export function setActorGetter(fnOrValue) {
  if (typeof fnOrValue === "function") actorGetter = fnOrValue;
  else actorGetter = () => fnOrValue;
}

/* ===== Base URL / Timeout ===== */
const baseURL =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "/api";

const timeout =
  Number(
    (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_TIMEOUT) ||
      process.env.REACT_APP_API_TIMEOUT
  ) || 10000;

/* ===== Axios instance ===== */
const api = axios.create({
  baseURL,
  timeout,
  withCredentials: true,
  headers: { Accept: "application/json" },
});

/* ===== Helpers ===== */
const CORS_SAFE_REQ_HEADERS = new Set([
  "accept",
  "content-type",
  "authorization",
  "x-csrf-token",
  "x-actor",
  "x-request-id",
  "x-idempotency-key",
]);

function isFormData(data) {
  return typeof FormData !== "undefined" && data instanceof FormData;
}

function ensureJsonContentType(config) {
  const method = String(config.method || "get").toUpperCase();
  if (!["POST", "PUT", "PATCH"].includes(method)) return;

  if (isFormData(config.data)) {
    if (config.headers) delete config.headers["Content-Type"];
    return;
  }
  if (!config.headers || !config.headers["Content-Type"]) {
    config.headers = config.headers || {};
    config.headers["Content-Type"] = "application/json;charset=utf-8";
  }
}

function isJsonResponse(res) {
  const ct =
    res?.headers?.["content-type"] ||
    res?.headers?.get?.("content-type") ||
    "";
  return typeof ct === "string" && ct.toLowerCase().includes("application/json");
}

/* ===== Interceptores ===== */
api.interceptors.request.use(
  async (config) => {
    config.headers = config.headers || {};

    // remove headers que não são CORS-safe
    for (const k of Object.keys(config.headers)) {
      const key = k.toLowerCase();
      if (!CORS_SAFE_REQ_HEADERS.has(key)) delete config.headers[k];
    }

    // CSRF (se existir meta no DOM)
    try {
      if (typeof document !== "undefined") {
        const csrf = document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content");
        if (csrf) config.headers["X-CSRF-Token"] = csrf;
      }
    } catch {}

    // Authorization via tokenGetter
    if (tokenGetter) {
      try {
        const t = await tokenGetter();
        if (t) config.headers.Authorization = `Bearer ${t}`;
      } catch {}
    }

    // X-Actor
    if (actorGetter) {
      try {
        const actor = await actorGetter();
        if (actor) config.headers["X-Actor"] = String(actor);
      } catch {}
    }

    ensureJsonContentType(config);
    return config;
  },
  (err) => Promise.reject(err)
);

api.interceptors.response.use(
  (res) => {
    const tz =
      res?.headers?.["x-timezone"] ||
      res?.headers?.["X-Timezone"] ||
      res?.headers?.get?.("x-timezone");
    if (tz) setTZ(tz);

    if (isJsonResponse(res)) res.data = attachDateKeys(res.data);
    return res;
  },
  (err) => {
    const message =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      (err?.code === "ERR_NETWORK" ? "Falha de rede ou CORS" : null) ||
      err?.message ||
      "Erro de rede";

    return Promise.reject({ ...err, message });
  }
);

export default api;
